// iter-321: Reusable Task Progress slider (5 pills: 25/50/75/90/100) plus a
// status dropdown. Used on Edit BOQ (admin) and Public Running-Bill (vendor).
// When admin clicks a pill, we call the admin endpoint; when vendor clicks,
// the parent passes a vendor-specific handler.
import { useState, useEffect } from 'react';
import { Check } from 'lucide-react';

const PILLS = [25, 50, 75, 90, 100];
const STATUSES = [
  { value: 'not_started', label: 'Not Started', tone: 'text-stone-600 bg-stone-100' },
  { value: 'in_progress', label: 'In Progress', tone: 'text-amber-700 bg-amber-100' },
  { value: 'done', label: 'Done', tone: 'text-emerald-700 bg-emerald-100' },
];

export default function TaskProgressSlider({
  itemId,
  progress = 0,
  status = 'not_started',
  source,          // 'admin' | 'vendor' | undefined
  updatedBy,
  updatedAt,
  readOnly = false,
  onChange,        // async (next: {progress?, progress_status?}) => void
  compact = false, // tighter spacing for inline use
}) {
  const [busy, setBusy] = useState(false);
  const [localProgress, setLocalProgress] = useState(progress);
  const [localStatus, setLocalStatus] = useState(status);

  // iter-338n: sync local state when the parent supplies an updated prop —
  // otherwise the slider stays "Not Started" forever even after the backend
  // SSOT correctly transitions the status to 'in_progress' / 'done'.
  useEffect(() => { setLocalProgress(progress); }, [progress]);
  useEffect(() => { setLocalStatus(status); }, [status]);

  const handlePill = async (val) => {
    if (readOnly || busy) return;
    const next = val === localProgress ? 0 : val; // click again to clear
    setLocalProgress(next);
    setBusy(true);
    try {
      await onChange?.({ progress: next });
    } finally {
      setBusy(false);
    }
  };

  const handleStatus = async (val) => {
    if (readOnly || busy) return;
    setLocalStatus(val);
    setBusy(true);
    try {
      await onChange?.({ progress_status: val });
    } finally {
      setBusy(false);
    }
  };

  const statusMeta = STATUSES.find(s => s.value === localStatus) || STATUSES[0];

  return (
    <div className={`flex flex-col gap-1.5 ${compact ? 'text-xs' : 'text-sm'}`} data-testid={`progress-slider-${itemId}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <div className="inline-flex items-center rounded-md border border-stone-300 overflow-hidden">
          {PILLS.map((p) => {
            const active = localProgress >= p;
            // iter-325: Unified colour bands. The active band = the pill we're on.
            let activeCls = 'bg-stone-300 text-stone-700';
            if (p >= 100) activeCls = 'bg-purple-600 text-white';
            else if (p >= 90) activeCls = 'bg-blue-800 text-white';
            else if (p >= 75) activeCls = 'bg-green-700 text-white';
            else if (p >= 50) activeCls = 'bg-green-400 text-white';
            else if (p >= 25) activeCls = 'bg-red-500 text-white';
            return (
              <button
                key={p}
                type="button"
                disabled={readOnly || busy}
                onClick={() => handlePill(p)}
                className={`px-2 py-1 font-medium transition-colors ${active ? activeCls : 'bg-white text-stone-600 hover:bg-stone-50'} ${readOnly ? 'opacity-60 cursor-not-allowed' : ''}`}
                data-testid={`progress-pill-${itemId}-${p}`}
                title={`${p}% Done`}
              >
                {p}
              </button>
            );
          })}
        </div>

        <select
          value={localStatus}
          disabled={readOnly || busy}
          onChange={(e) => handleStatus(e.target.value)}
          className={`px-2 py-1 rounded-md border border-stone-300 ${statusMeta.tone} font-medium focus:ring-2 focus:ring-amber-500 ${readOnly ? 'opacity-60 cursor-not-allowed' : ''}`}
          data-testid={`progress-status-${itemId}`}
        >
          {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>

        {source === 'vendor' && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 text-[10px] font-semibold border border-purple-300" data-testid={`vendor-update-badge-${itemId}`}>
            <Check size={10} />
            Vendor update
          </span>
        )}
      </div>

      {(updatedBy || updatedAt) && (
        <p className="text-[11px] text-stone-400">
          {updatedBy ? `By ${updatedBy}` : ''}{updatedBy && updatedAt ? ' · ' : ''}{updatedAt ? new Date(updatedAt).toLocaleString() : ''}
        </p>
      )}
    </div>
  );
}
